import AccordionDetails from "@mui/material/AccordionDetails";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Grid,
  FormControl,
  TextField,
  Checkbox,
  Button,
  InputLabel,
} from "@mui/material";
import "./dsInvestigation.css";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  DSInvestigationInitValue,
  DSInvestigationSchema,
} from "../../../schema/dsInvestigation";
import { FieldArray, FormikProvider, useFormik } from "formik";
import dayjs from "dayjs";
import {
  AS_INVESTIGATION_WIZARD_STEPS,
  DS_ALL_INVESTIGATION_FIELDS_GROUPED,
  EFormAction,
  EUploadKey,
  ErrorMessages,
  EShortageInvestigationStatus,
  EASInvestigationFieldsErrorMessage,
  STEPS_REQUIRED_MAP,
  FREIGHT_LOCATED_STEP_MAP,
  INVESTIGATION_DONE_STEP_MAP,
  MAX_FILE_SIZE,
  MAX_LENGTH_MANIFEST_NUMBER,
  MAX_LENGTH_MEDIUM_ENTRY,
  MAX_LENGTH_PHONE_NUMBER,
  MAX_LENGTH_SUMMARY_TEXT,
  STEP_DAY_MAPPING,
  STEP_STATUS_MAP,
  FORM_STYLES,
} from "../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { PopoutNotification } from "../../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../../AppState/NotificationType";
import { isFormValueChanged } from "../../../AppState/isFormValueChanged";
import Loader from "../../../components/loader/loader";
import {
  chunkArray,
  combineTowJsonObject,
  compareObjects,
  handleKeyDown,
  handleNumberMaxLength,
  handlePaste,
  handleTextFieldKeyDown,
  htmlToImage,
  trimStrings,
} from "../../../helpers";
import {
  EDSInvestigationFields,
  IDSInvestigation,
  IDSFreightTerminalLeadInquiries,
  ICallSameDaysDataset,
} from "../../../models/dsException";
import _, { debounce } from "lodash";
import DSInvestigationTemplate from "../../../templates/dsInvestigationTemplate";
import { FileUploadAPI } from "../../../API/fileUpload";
import { IUploadFile } from "../../../models/master";
import { ShortageInvestigationCreateUpdateAPI } from "../../../API/shortageInvestigationCreateUpdate";
import DSDay5Template from "../../../templates/dsDay5Template";
import CustomDialog from "../../../components/confirmationPopup/confirmationPopup";
import TooltipWithClickAway from "../../../components/terminalPicker/TooltipWithClickAway";
import { hasCompleteShortagesPermissionSelector } from "../../../AppState/selectors/userPermissionsSelectors";
import Day15Investigation from "../sharedShortageComponent/Day15Investigation";
import { InvestigationStepper } from "../sharedShortageComponent/InvestigationStepper";
import NoteBox from "../../../components/noteBox/NoteBox";

type Props = {
  isFormEditMode?: boolean;
  setInvestigationStatus: (status: number) => void;
  osdDetails?: any;
};

const DsInvestigation: React.FC<Props> = ({
  osdDetails,
  isFormEditMode,
  setInvestigationStatus,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  // Prepare the list of all fields to iterate
  const allFields = DS_ALL_INVESTIGATION_FIELDS_GROUPED.flat();

  const investigationDetails = useMemo(() => osdDetails, [osdDetails]);
  const [activeStep, setActiveStep] = useState<number>(
    STEP_STATUS_MAP[EShortageInvestigationStatus.exceptionEntry],
  );
  const [pageLoading, setPageLoading] = React.useState<boolean>(false);
  const [formFieldsDisabled, setFormFieldsDisabled] = React.useState<
    boolean | undefined
  >(isFormEditMode);
  const [resetTimestamps, setResetTimestamps] = useState<string>("");
  const [formKey, setFormKey] = useState<string>(Date.now().toString());
  const [open, setOpen] = useState<boolean>(false);
  const [isFreightLocated, setIsFreightLocated] = useState<boolean>(false);
  const [status, setStatus] = React.useState<number>(1);
  const [lastStatusNumber, setLastStatusNumber] = React.useState<number>(1);
  const [invalidFields, setInvalidFields] = React.useState<string[]>([]);
  const [addButtonDisabled, setAddButtonDisabled] = useState(false);
  const [proInfoAddButtonDisabled, setProInfoAddButtonDisabled] =
    useState(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [shouldShowDayCompleteButton, setShouldShowDayCompleteButton] =
    useState<boolean>(true);
  const activeDay =
    STEP_DAY_MAPPING[activeStep as keyof typeof STEP_DAY_MAPPING];
  const [isEditMode, setIsEditMode] = React.useState<boolean | undefined>(
    isFormEditMode,
  );
  const [dateKey, setDateKey] = React.useState<string>(Date.now().toString());
  const isPrivilegedUser: boolean = useSelector(
    hasCompleteShortagesPermissionSelector,
  );

  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isShowSendAPB, setIsShowSendAPB] = useState<boolean>(false);

  const [dialogOpen, setDialogOpen] = useState(false); // State to manage dialog open/close

  const adjustedStatus = isFreightLocated
    ? FREIGHT_LOCATED_STEP_MAP[
        lastStatusNumber as keyof typeof FREIGHT_LOCATED_STEP_MAP
      ]
    : STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP];

  const shouldShowCompleteButton =
    formFieldsDisabled &&
    isPrivilegedUser &&
    status === EShortageInvestigationStatus.pendingSrApproval &&
    adjustedStatus === activeStep;

  const shouldShowFreightButton =
    formFieldsDisabled &&
    !isFreightLocated &&
    STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] === activeStep &&
    ![
      EShortageInvestigationStatus.complete,
      EShortageInvestigationStatus.exceptionEntry,
      EShortageInvestigationStatus.pendingSrApproval,
    ].includes(status);

  const [initValues, setInitValues] = React.useState<IDSInvestigation>(
    _.mergeWith(
      {},
      DSInvestigationInitValue,
      investigationDetails,
      combineTowJsonObject,
    ),
  );

  const showNotification = useCallback(
    (type: any, message?: string) => {
      // Handle both direct notification and response object
      if (typeof type === "object" && type !== null) {
        const res = type;
        const errors = _.get(res, "errors", []);
        const notificationType =
          errors.length > 0
            ? NOTIFICATION_TYPE.ERROR
            : NOTIFICATION_TYPE.SUCCESS;
        const msg =
          errors.length > 0
            ? errors.join(", ")
            : (res.result ?? "OS&D investigation image uploaded successfully");
        dispatch(
          PopoutNotification(notificationType, msg, Date.now().toString()),
        );
      } else {
        dispatch(
          PopoutNotification(type, message ?? "", Date.now().toString()),
        );
      }
    },
    [dispatch],
  );

  const handleUploadError = useCallback(
    (error: any) => {
      showNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof error === "string"
          ? error
          : "OS&D investigation image uploading failed",
      );
    },
    [showNotification],
  );

  // If active day investigation is completed the checkboxes is disabled of day
  const isControlDisabled = useCallback(
    (fieldName: keyof IDSInvestigation) => {
      const value = "Y";
      const isChecked =
        typeof initValues[fieldName] === "string" &&
        initValues[fieldName] === value;
      if (
        (status > EShortageInvestigationStatus.day20InProgress ||
          isFreightLocated) &&
        isChecked
      ) {
        return true;
      }
      if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
        return;
      if (isChecked) {
        return true;
      }
    },
    [status, isFreightLocated, activeStep, initValues],
  );

  const isShowAddButton = () =>
    !(
      (formFieldsDisabled ?? false) ||
      status === EShortageInvestigationStatus.complete
    );

  // Enable fields in edit mode upon clicking the edit button
  const enableEditing = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault(); // Prevents the default form submission behavior
      setFormFieldsDisabled(false); // Enables the form fields for editing
    },
    [],
  );

  // Initialize form handling using Formik
  // This setup uses Formik to manage the form state, validation, and submission process.
  const form = useFormik({
    validationSchema: DSInvestigationSchema[activeStep], // Validation schema for the form fields
    validateOnChange: false, // Disable validation on field change
    initialValues: initValues, // Initial values for the form fields
    onSubmit: () => {},
  });

  // Destructure various handlers and values provided by Formik for easy use within the component
  const {
    values, // Current values of the form fields
    handleChange, // Handler for changing form field values
    handleBlur, // Handler for managing field blur events
    validateField, // Function to validate a specific field
    setFieldValue, // Function to set the value of a specific field
    validateForm, // Function to validate the entire form
    resetForm, // Function to reset the form to its initial values
    errors,
  } = form;

  const callSameDaysChunks = useMemo(
    () => chunkArray(values?.callSameDaysDataset, 8),
    [values?.callSameDaysDataset],
  );

  const uploadInvestigation = useCallback(
    async (options?: { freightLocated?: boolean; doneTillDay?: number }) => {
      setPageLoading(true);

      const freightLocated = options?.freightLocated ?? isFreightLocated;
      const doneTillDay = options?.doneTillDay ?? lastStatusNumber;

      let ids = AS_INVESTIGATION_WIZARD_STEPS;
      if (freightLocated) {
        ids = ids.slice(
          0,
          INVESTIGATION_DONE_STEP_MAP[
            doneTillDay as keyof typeof INVESTIGATION_DONE_STEP_MAP
          ],
        );
      }

      const imagePromises: Promise<File>[] = ids.flatMap((day, index) => {
        if (day === "Day 5") {
          const callPromises = callSameDaysChunks.map((_c, chunkIndex) =>
            htmlToImage(
              `investigation-${day}-callSameDays-${chunkIndex}`,
              `${day}-page-${chunkIndex + 1}.jpeg`,
              MAX_FILE_SIZE,
            ),
          );

          const freightPromise = htmlToImage(
            `investigation-${day}-freightTerminal-${index}`,
            `${day}-freightTerminal-page-${index + 1}.jpeg`,
            MAX_FILE_SIZE,
          );

          return [...callPromises, freightPromise];
        }

        const quality = day === "Day 3" ? MAX_FILE_SIZE : undefined;
        return [
          htmlToImage(`investigation-${day}`, `${index + 1}.jpeg`, quality),
        ];
      });

      try {
        const images = await Promise.all(imagePromises);

        const reqInput: IUploadFile = {
          osdNumber: investigationDetails?.osdNumber,
          docType: "FX",
          uploadKey: EUploadKey.pro,
        };

    images.forEach((image, index) => {
       console.log("DS ImageUploader Generated image:", index, URL.createObjectURL(image));
    });
        const res = await FileUploadAPI(images, reqInput);
        showNotification(res);
      } catch (error) {
        handleUploadError(error);
      } finally {
        setPageLoading(false);
      }
    },
    [
      callSameDaysChunks,
      handleUploadError,
      investigationDetails?.osdNumber,
      isFreightLocated,
      lastStatusNumber,
      showNotification,
    ],
  );

  const getPageIndex = (index: number, callSameDaysChunks: any[]) => {
    if (index === 1) {
      return callSameDaysChunks[0].length;
    }
    if (index === 2) {
      return [...callSameDaysChunks[0], ...callSameDaysChunks[1]].length;
    }
    return 0;
  };
  /**
   * Validates a field based on the value of a dependent field and the current step in the process.
   *
   * - Adds the field to `invalidFields` if the dependent field matches the expected value (`val`),
   *   the field is empty, and it is not already marked as invalid.
   * - Removes the field from `invalidFields` if the conditions are not met.
   */
  const isValidDependedField = (
    fieldName: keyof IDSInvestigation,
    dependedField: keyof IDSInvestigation,
    activeStep: number,
    val: string = "Y",
  ) => {
    // Exit early if the status step is not reached
    // if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP ] > activeStep) return;
    if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;

    const fieldValue =
      typeof values[fieldName] === "object" && values[fieldName] !== null
        ? JSON.stringify(values[fieldName])
        : values[fieldName]?.toString();
    const dependedValue = values[dependedField];

    // Add field to invalidFields if conditions are met
    setInvalidFields((prevInvalidFields) => {
      // Determine if we should add or remove the fieldName
      const shouldAdd = dependedValue === val && !fieldValue;

      if (shouldAdd) {
        // Add the fieldName to the list if it's not already present
        return prevInvalidFields.includes(fieldName)
          ? prevInvalidFields
          : [...prevInvalidFields, fieldName];
      } else {
        // Remove the fieldName from the list if it's present
        return prevInvalidFields.filter((field) => field !== fieldName);
      }
    });
    // Update state with modified invalid fields
    // setInvalidFields(Array.from(updatedInvalidFields));
  };

  /**
   * Adds or removes field names related to the investigation details from the `invalidFields` list based on the provided type.
   *
   * - **Type "add"**: Adds fields with names based on the current length of `freightTerminalLeadInquiries` to `invalidFields`.
   * - **Type "remove"**: Removes fields with names based on the current length of `freightTerminalLeadInquiries` from `invalidFields`.
   *
   */
  const addRemove = (type: "add" | "remove" = "add") => {
    if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;
    // Determine the length based on the type
    const length =
      (values?.freightTerminalLeadInquiries?.length ?? 0) -
      (type === "add" ? 0 : 1);

    const fields = [
      `terminal${length}`,
      `talkedTo${length}`,
      `updates${length}`,
    ];

    setInvalidFields((prevFields) =>
      type === "add"
        ? [...prevFields, ...fields]
        : prevFields.filter((field) => !fields.includes(field)),
    );
  };

  const proInfoAddRemove = (type: "add" | "remove" = "add") => {
    // Exit if the current step is not active
    if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
      return;
    // Determine the length based on the type
    const length =
      (values?.callSameDaysDataset?.length ?? 0) - (type === "add" ? 0 : 1);

    const fields = [`sameDayPro${length}`, `deliveryInfo${length}`];

    setInvalidFields((prevFields) =>
      type === "add"
        ? [...prevFields, ...fields]
        : prevFields.filter((field) => !fields.includes(field)),
    );
  };

  const renderCompletedError = useCallback(
    (
      fieldName: string,
      day: keyof typeof EASInvestigationFieldsErrorMessage,
      conditionalFlag: boolean = true,
    ) => {
      if (invalidFields.includes(fieldName) && conditionalFlag) {
        return (
          <span className="form-error">
            {EASInvestigationFieldsErrorMessage[day]}
          </span>
        );
      }
      return null;
    },
    [invalidFields],
  );

  const getErrorMessage = useCallback(
    (fieldName: keyof IDSInvestigation) => {
      if (
        errors[fieldName] &&
        isCompleting &&
        !invalidFields.includes(fieldName)
      ) {
        return (
          <span className="form-error">{errors[fieldName]?.toString()}</span>
        );
      }
      return null;
    },
    [errors, isCompleting, invalidFields],
  );

  const isValueExist = useCallback(
    (
      fieldName: string,
      index?: number,
      fieldArrayName: string = "freightTerminalLeadInquiries",
    ) => {
      // Exit if the current step is not active
      if (STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP] <= activeStep)
        return;

      const hasIndex = index !== undefined;
      const fieldKey = hasIndex ? `${fieldName}${index}` : fieldName;
      const value = hasIndex
        ? _.get(values, `${fieldArrayName}[${index}][${fieldName}]`, "")
        : (values[fieldName as keyof IDSInvestigation] ?? "");

      const initValue = hasIndex
        ? _.get(initValues, `${fieldArrayName}[${index}][${fieldName}]`, "")
        : initValues[fieldName as keyof IDSInvestigation];
      const isInvalid =
        (value == null || value === "") && // Covers null, undefined, and empty string
        ((index === undefined && initValue !== "" && initValue != null) ||
          index !== undefined); // Checks if initValue is neither an empty string, null, nor undefined

      setInvalidFields((prevInvalidFields) => {
        const alreadyInvalid = prevInvalidFields.includes(fieldKey);
        if (isInvalid) {
          return alreadyInvalid
            ? prevInvalidFields
            : [...prevInvalidFields, fieldKey];
        }
        return alreadyInvalid
          ? prevInvalidFields.filter((field) => field !== fieldKey)
          : prevInvalidFields;
      });
    },
    [status, activeStep, values, initValues],
  );

  const reset = useCallback(() => {
    resetForm({ values: initValues });
    setResetTimestamps(Date.now().toString());
    setInvalidFields([]);
  }, [initValues, resetForm]);

  const getCallSameDaysDataset = (checked: boolean) => {
    if (checked) {
      return _.isArray(values.callSameDaysDataset) &&
        values.callSameDaysDataset.length > 0
        ? values.callSameDaysDataset
        : DSInvestigationInitValue.callSameDaysDataset;
    }
    return [];
  };

  const getFreightTerminalLeadInquiries = (checked: boolean) => {
    if (checked) {
      return _.isArray(values.freightTerminalLeadInquiries) &&
        values.freightTerminalLeadInquiries.length > 0
        ? values.freightTerminalLeadInquiries
        : DSInvestigationInitValue.freightTerminalLeadInquiries;
    }
    return [];
  };

  const validateDSDay5Fields = useCallback(async () => {
    if (!values.freightTerminalLeadInquiries?.length) return;
    for (
      let index = 0;
      index < values.freightTerminalLeadInquiries.length;
      index++
    ) {
      const validateFields = ["terminal", "talkedTo", "updates"];
      for (const field of validateFields) {
        await validateField(`freightTerminalLeadInquiries[${index}].${field}`);
      }
    }
  }, [values.freightTerminalLeadInquiries, validateField]);

  const validateCallSameDaysDataset = useCallback(async () => {
    if (!values.callSameDaysDataset?.length) return;
    for (let index = 0; index < values.callSameDaysDataset.length; index++) {
      const validateFields = ["sameDayPro", "deliveryInfo"];
      for (const field of validateFields) {
        await validateField(`callSameDaysDataset[${index}].${field}`);
      }
    }
  }, [values.callSameDaysDataset, validateField]);

  const updateInitValue = (
    key: string,
    value: string | number | null | undefined,
  ) => {
    setInitValues((prevState) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  // handle confirmation pop while user want back according button
  const handleDialogOpen = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleFreightAndCompletion = useCallback(
    async (action: EFormAction) => {
      try {
        setPageLoading(true);
        const submitResponse = await ShortageInvestigationCreateUpdateAPI(
          {},
          investigationDetails?.osdNumber?.toString(),
          action,
          action === EFormAction.freightLocated
            ? activeDay.toString()
            : undefined,
        );
        const {
          status: statusNumber,
          investigationDoneTillTheDay: doneTillDay,
          freightLocated: freightLocatedFromResponse,
        } = submitResponse;
        setStatus(statusNumber);
        setLastStatusNumber(doneTillDay);
        setIsFreightLocated(freightLocatedFromResponse);
        setInvestigationStatus(statusNumber);

        setFormKey(Date.now().toString());
        let message;
        if (action === EFormAction.complete) {
          message = ErrorMessages.investigationNoteCompleted;
          uploadInvestigation({
            freightLocated: freightLocatedFromResponse || isFreightLocated,
            doneTillDay,
          });
        } else {
          setActiveStep(
            FREIGHT_LOCATED_STEP_MAP[
              doneTillDay as keyof typeof FREIGHT_LOCATED_STEP_MAP
            ],
          );
          message = ErrorMessages.freightLocatedSuccess;
        }
        showNotification(NOTIFICATION_TYPE.SUCCESS, message);
      } catch (error) {
        showNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string"
            ? error
            : `OS&D investigation notes submit failed`,
        );
      } finally {
        setPageLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      investigationDetails?.osdNumber,
      activeDay,
      showNotification,
      setInvestigationStatus,
      uploadInvestigation,
      isFreightLocated,
    ],
  );

  // Close the confirmation modal pop and navigate user to back All Shortage listing page if user confirmed
  const handleDialogClose = useCallback(
    (flag?: string) => {
      setDialogOpen(false);
      if (flag === "yes") {
        handleFreightAndCompletion(EFormAction.freightLocated);
      }
    },
    [handleFreightAndCompletion],
  );

  const saveCompleteBtnClassName = () => {
    if (invalidFields?.length > 0) return "disable-btn disable entry ml";
    return "entry ml";
  };

  /**
   * Handles saving or submitting the OS&D investigation notes based on the provided action.
   *
   */
  const saveOrSubmit = async (action: EFormAction) => {
    if (invalidFields?.length > 0) return;
    setIsCompleting(false);

    if (await shouldAbortDueToErrors(action)) return;
    if (shouldAbortDueToInvalidFields()) return;
    if (focusOnRequiredField()) return;

    let reqParams = createRequestParams();
    reqParams = _.omitBy(reqParams, _.isUndefined);

    if (_.isEmpty(reqParams)) return;

    // Trim all of string variables
    reqParams = trimStrings(reqParams);
    setPageLoading(true);

    submitRequest(reqParams, action);
  };

  const shouldAbortDueToErrors = async (action: EFormAction) => {
    // Ensure the form is fully validated before proceeding
    const errorList = await form.validateForm();
    const requiredSteps =
      STEPS_REQUIRED_MAP[lastStatusNumber as keyof typeof STEPS_REQUIRED_MAP] ||
      [];

    // Check if the action is either 'submit' or 'save' (validate the save form if the Investigation Day is already completed) with valid conditions

    const isValidAction =
      action === EFormAction.submit ||
      (action === EFormAction.save && requiredSteps.includes(activeStep));

    // If the action is valid and there are errors, abort submission
    if (isValidAction && Object.keys(errorList).length > 0) {
      setIsCompleting(true);
      // Focus on the first error field if there are validation errors
      const firstErrorField = Object.keys(errors)[0];
      document.getElementsByName(firstErrorField)[0]?.focus();
      return true; // Abort due to validation errors
    }

    // If there are no errors, proceed with submission
    return false;
  };

  const focusOnRequiredField = () => {
    if (activeStep === STEP_STATUS_MAP[6]) {
      const isAllSameDayProValid = values?.callSameDaysDataset?.every(
        (item, index) => {
          if (Number.isNaN(+(item?.sameDayPro ?? ""))) {
            // Focus on the invalid field
            const field = document.getElementsByName(
              `callSameDaysDataset[${index}].sameDayPro`,
            )[0];
            field?.focus();
            return false;
          }
          return true;
        },
      );

      if (!isAllSameDayProValid) {
        return true; // Prevent save
      }
    }
    if (activeStep === STEP_STATUS_MAP[4] && values.pumManifest === 0) {
      document.getElementsByName("pumManifest")[0].focus();
      return true;
    }
    return false;
  };

  const shouldAbortDueToInvalidFields = () => {
    return invalidFields?.length > 0;
  };

  const createRequestParams = () => {
    let reqParams: any = structuredClone(values);
    const deleteFields: string[] = [];
    const allFields = DS_ALL_INVESTIGATION_FIELDS_GROUPED;
    if (activeStep >= 0 && activeStep < allFields.length) {
      reqParams = Object.fromEntries(
        Object.entries(reqParams).filter(([key]) =>
          allFields[activeStep].includes(key),
        ),
      );
    }

    handleConditionalFields(reqParams, deleteFields);
    deleteFields.forEach((field) => delete reqParams[field]);

    formatRequestParams(reqParams);

    const { freightTerminalLeadInquiries, callSameDaysDataset, ...data } =
      reqParams;

    reqParams = {
      data,
      ...(callSameDaysDataset ? { callSameDaysDataset } : {}),
      ...(freightTerminalLeadInquiries ? { freightTerminalLeadInquiries } : {}),
      ...(isShowSendAPB ? { attachmentImageUrls: selectedImages } : {}),
    };

    return reqParams;
  };

  const handleConditionalFields = (reqParams: any, deleteFields: string[]) => {
    if (reqParams["contactTerminals"]) {
      if (reqParams["contactTerminals"] === "N") {
        deleteFields.push("freightTerminalLeadInquiries");
      }
    } else {
      deleteFields.push("contactTerminals", "freightTerminalLeadInquiries");
    }

    if (reqParams["callSameDays"]) {
      if (reqParams["callSameDays"] === "N") {
        deleteFields.push("callSameDaysDataset");
      }
    } else {
      deleteFields.push("callSameDays", "callSameDaysDataset");
    }
  };

  const formatRequestParams = (reqParams: any) => {
    // Convert specific fields

    // Convert specific fields to the correct types

    if (reqParams.callSameDaysDataset) {
      reqParams.callSameDaysDataset = (reqParams.callSameDaysDataset ?? []).map(
        (obj: ICallSameDaysDataset) => ({
          sameDayPro: obj.sameDayPro === 0 ? "" : obj.sameDayPro?.toString(),
          deliveryInfo: obj.deliveryInfo === null ? "" : obj.deliveryInfo,
        }),
      );
    } else {
      delete reqParams.callSameDaysDataset;
    }
    if (reqParams.consigneePhone) {
      reqParams.consigneePhone = reqParams.consigneePhone.toString();
    }

    if (reqParams.shipperPhone) {
      reqParams.shipperPhone = reqParams.shipperPhone.toString();
    }

    // Convert `pumManifest` to an integer if it exists and is a valid number
    if (reqParams.pumManifest && !Number.isNaN(reqParams.pumManifest)) {
      reqParams.pumManifest = Number.parseInt(reqParams.pumManifest);
    } else {
      delete reqParams.pumManifest;
    }
  };

  const submitRequest = (reqParams: any, action: EFormAction) => {
    ShortageInvestigationCreateUpdateAPI(
      reqParams,
      investigationDetails?.osdNumber?.toString(),
      action,
      activeDay.toString(),
    )
      .then((res) => handleSuccessfulSubmission(res, action))
      .catch((error) => handleSubmissionError(error, action));
  };

  const handleSuccessfulSubmission = (res: any, action: EFormAction) => {
    if (action === EFormAction.save) setFormFieldsDisabled(true);
    setIsCompleting(false);
    processRequestFields();
    displaySuccessMessage(action);

    handleStatusAndUIUpdates(res);

    setPageLoading(false);
    validateForm(values);
    dispatch(isFormValueChanged(false));
  };

  const handleSubmissionError = useCallback(
    (err: any, action: EFormAction) => {
      setPageLoading(false);
      showNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof err === "string"
          ? err
          : `OS&D investigation notes ${action} failed`,
      );
    },
    [showNotification],
  );

  const processFreightTerminalLeadInquiries = (reqParams: any) => {
    reqParams.freightTerminalLeadInquiries = (
      reqParams.freightTerminalLeadInquiries ?? []
    ).map((obj: IDSFreightTerminalLeadInquiries) => ({
      terminal: obj.terminal === null ? "" : obj.terminal,
      talkedTo: obj.talkedTo === null ? "" : obj.talkedTo,
      updates: obj.updates === null ? "" : obj.updates,
    }));
  };

  const processCallSameDaysDataset = (reqParams: any) => {
    reqParams.callSameDaysDataset = (reqParams.callSameDaysDataset ?? []).map(
      (obj: ICallSameDaysDataset) => ({
        sameDayPro:
          obj.sameDayPro === null || obj.sameDayPro === "0"
            ? ""
            : obj.sameDayPro,
        deliveryInfo: obj.deliveryInfo === null ? "" : obj.deliveryInfo,
      }),
    );
  };
  const processRequestFields = () => {
    const reqParams: any = trimStrings(values);
    processFreightTerminalLeadInquiries(reqParams);
    processCallSameDaysDataset(reqParams);

    Object.keys(reqParams).forEach((field: string) => {
      if (allFields.includes(field)) {
        const value =
          field === "pumManifest" && reqParams[field] === 0
            ? undefined
            : reqParams[field];
        setFieldValue(field, value);
        updateInitValue(field, value);
      }
    });
  };

  const displaySuccessMessage = useCallback(
    (action: EFormAction) => {
      const message =
        action === EFormAction.save
          ? ErrorMessages.investigationNoteSaved
          : `OS&D investigation day ${activeDay} notes have been completed successfully`;

      showNotification(NOTIFICATION_TYPE.SUCCESS, message);
    },
    [activeDay, showNotification],
  );

  const handleStatusAndUIUpdates = (res: any) => {
    const statusNumber = res.status;
    if (
      status === statusNumber ||
      statusNumber > EShortageInvestigationStatus.day20InProgress ||
      (status === EShortageInvestigationStatus.exceptionEntry &&
        statusNumber === EShortageInvestigationStatus.day1InProgress)
    ) {
      setFormFieldsDisabled(true);
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
    setStatus(statusNumber);
    setIsFreightLocated(res.freightLocated);
    setInvestigationStatus(statusNumber);
    setLastStatusNumber(res.investigationDoneTillTheDay);
    if (res.freightLocated) {
      setLastStatusNumber(res.investigationDoneTillTheDay);
      setActiveStep(
        FREIGHT_LOCATED_STEP_MAP[
          res.investigationDoneTillTheDay as keyof typeof FREIGHT_LOCATED_STEP_MAP
        ],
      );
    } else {
      setActiveStep(
        STEP_STATUS_MAP[statusNumber as keyof typeof STEP_STATUS_MAP],
      );
    }
  };

  const hasError = useCallback(
    (field: string, condition: boolean = true) => {
      return Boolean(_.get(errors, field)) && isCompleting && condition;
    },
    [errors, isCompleting],
  );

  // Reusable component for checkbox questions with conditional text field
  const renderCheckboxQuestion = useCallback(
    (
      fieldName: keyof IDSInvestigation,
      textFieldName: keyof IDSInvestigation,
      label: string | JSX.Element,
      tooltip?: { text: string; key: string },
      isExistingValue: boolean = true,
    ) => {
      // Determine the day based on active step
      const stepToDayMap: Record<
        number,
        keyof typeof EASInvestigationFieldsErrorMessage
      > = {
        [STEP_STATUS_MAP[3]]: "day2",
        [STEP_STATUS_MAP[4]]: "day3",
      };
      const dayLabel: keyof typeof EASInvestigationFieldsErrorMessage =
        stepToDayMap[activeStep] ?? "day4";

      return (
        <FormControl className="radio-group question-group ">
          <FormLabel>
            <FormControlLabel
              className="checkbox-shoartage"
              control={
                <Checkbox
                  disableRipple
                  checked={values[fieldName] === "Y"}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;
                    setFieldValue(fieldName, checked ? "Y" : "N");
                    setFieldValue(
                      textFieldName,
                      checked
                        ? (initValues[textFieldName] ?? values[textFieldName])
                        : "",
                      true,
                    );
                  }}
                  disabled={isControlDisabled(fieldName) ?? formFieldsDisabled}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />
              }
              label=""
            />{" "}
            {label} <sup>*</sup>{" "}
            {tooltip && (
              <TooltipWithClickAway
                tooltipText={tooltip.text}
                tooltipKey={tooltip.key}
              />
            )}
          </FormLabel>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12} className="all-short-entry">
              {values[fieldName] === "Y" && (
                <TextField
                  autoComplete="off"
                  name={textFieldName}
                  value={values[textFieldName] ?? ""}
                  fullWidth
                  placeholder="Describe Steps Taken"
                  error={hasError(textFieldName)}
                  inputProps={{
                    maxLength: MAX_LENGTH_SUMMARY_TEXT,
                  }}
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                    if (isExistingValue) {
                      isValueExist(textFieldName);
                    }
                  }}
                  disabled={formFieldsDisabled}
                  variant="outlined"
                />
              )}
            </Grid>
          </Grid>
          {getErrorMessage(fieldName)}
          {getErrorMessage(textFieldName)}
          {renderCompletedError(textFieldName, dayLabel)}
        </FormControl>
      );
    },
    [
      values,
      initValues,
      formFieldsDisabled,
      handleChange,
      handleBlur,
      setFieldValue,
      activeStep,
      isControlDisabled,
      hasError,
      isValueExist,
      getErrorMessage,
      renderCompletedError,
    ],
  );

  // Reusable component for simple text fields with standard validation
  const renderSimpleTextField = useCallback(
    (
      fieldName: keyof IDSInvestigation,
      label: string,
      dayStep: keyof typeof EASInvestigationFieldsErrorMessage,
      options?: {
        placeholder?: string;
        maxLength?: number;
        gridSize?: { md: number; xs: number };
        gridClassName?: string;
        onBlurCallback?: () => void;
      },
    ) => {
      const {
        placeholder = "Describe Steps Taken",
        maxLength = MAX_LENGTH_SUMMARY_TEXT,
        gridSize = { md: 12, xs: 12 },
        gridClassName = "pt-0",
        onBlurCallback,
      } = options ?? {};

      return (
        <Grid item md={gridSize.md} xs={gridSize.xs} className={gridClassName}>
          <FormControl className="radio-group question-group ">
            <FormLabel className="mb-10">
              {label} <sup>*</sup>
            </FormLabel>
            <TextField
              autoComplete="off"
              name={fieldName}
              value={values[fieldName] ?? ""}
              fullWidth
              error={hasError(fieldName)}
              inputProps={{ maxLength }}
              onChange={handleChange}
              onBlur={(e) => {
                handleBlur(e);
                onBlurCallback?.();
              }}
              disabled={formFieldsDisabled}
              variant="outlined"
              placeholder={placeholder}
            />
          </FormControl>
          {getErrorMessage(fieldName)}
          {renderCompletedError(fieldName, dayStep)}
        </Grid>
      );
    },
    [
      values,
      formFieldsDisabled,
      handleChange,
      handleBlur,
      hasError,
      getErrorMessage,
      renderCompletedError,
    ],
  );

  const getValidationErrorMessage = useCallback(
    (field: keyof IDSFreightTerminalLeadInquiries, index: number) => {
      const errorMessage = _.get(
        errors,
        `freightTerminalLeadInquiries[${index}].${field}`,
        "",
      ).toString();
      const isFieldInvalid = invalidFields.includes(`${field}${index}`);
      const isFieldEmpty =
        !values?.freightTerminalLeadInquiries?.[index]?.[field]?.trim();

      if (isCompleting && !isFieldInvalid && errorMessage) {
        return <span className="form-error">{errorMessage}</span>;
      }

      if (isFieldInvalid && isFieldEmpty) {
        return (
          <span className="form-error">
            {EASInvestigationFieldsErrorMessage.day5}
          </span>
        );
      }

      return null;
    },
    [errors, invalidFields, values, isCompleting],
  );

  const renderF18Note = (fieldName: keyof IDSInvestigation) => {
    const dayF18Notes: any = {
      day1F18Note: "day1",
      day2F18Note: "day2",
      day3F18Note: "day3",
      day4F18Note: "day4",
      day5F18Note: "day5",
      day10F18Note: "day10",
      day15F18Note: "day15",
      day20F18Note: "day20",
    };
    const day: string = dayF18Notes[fieldName];
    return (
      <Grid item md={12} xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
            F18 Notes <sup>*</sup>
          </InputLabel>
          <TextField
            autoComplete="off"
            multiline
            rows={2}
            name={fieldName}
            key={fieldName}
            id={fieldName}
            value={values[fieldName]}
            onChange={handleChange}
            onBlur={(e) => {
              handleBlur(e);
              isValueExist(fieldName);
            }}
            placeholder="Enter F18 Notes"
            fullWidth
            inputProps={{ maxLength: 300 }}
            disabled={formFieldsDisabled}
            variant="outlined"
          />
        </FormControl>
        {getErrorMessage(fieldName)}
        {renderCompletedError(
          fieldName,
          day as keyof typeof EASInvestigationFieldsErrorMessage,
        )}
      </Grid>
    );
  };

  /**
   * Validates the form on changes to `values` and
   * updates the add button state based on validation results.
   */
  useEffect(() => {
    if (!values) return; // Skip validation if no values

    const debouncedValidation = debounce(async () => {
      try {
        const formErrors = await validateForm();
        const freightTerminalLeadInquiriesErrors =
          formErrors.freightTerminalLeadInquiries;

        if (freightTerminalLeadInquiriesErrors?.length) {
          validateDSDay5Fields();
          setAddButtonDisabled(true);
        } else {
          setAddButtonDisabled(false);
        }
      } catch (error) {
        console.error("Validation error occurred:", error);
        setAddButtonDisabled(true);
      }
    }, 300);

    debouncedValidation();

    return () => debouncedValidation.cancel();
  }, [values, validateForm, validateDSDay5Fields]);

  useEffect(() => {
    if (!values) return; // Skip validation if no values

    const debouncedValidation = debounce(async () => {
      try {
        const formErrors = await validateForm();
        const proDeliveryInfoErrors = formErrors.callSameDaysDataset;

        if (proDeliveryInfoErrors?.length) {
          validateCallSameDaysDataset();
          setProInfoAddButtonDisabled(true);
        } else {
          setProInfoAddButtonDisabled(false);
        }
      } catch (error) {
        console.error("Error during validation:", error);
        setProInfoAddButtonDisabled(true);
      }
    }, 300);

    debouncedValidation();

    return () => debouncedValidation.cancel();
  }, [values, validateForm, validateCallSameDaysDataset]);

  const ensureDatasetsExist = (details: IDSInvestigation) => {
    if (
      !details.freightTerminalLeadInquiries?.length &&
      details.contactTerminals === "Y"
    ) {
      details.freightTerminalLeadInquiries = [
        { terminal: undefined, talkedTo: undefined, updates: undefined },
      ];
    }
    if (!details.callSameDaysDataset?.length && details.callSameDays === "Y") {
      details.callSameDaysDataset = [
        { sameDayPro: undefined, deliveryInfo: undefined },
      ];
    }
  };

  const updateFreightTerminalLeadInquiries = (details: IDSInvestigation) => {
    details.freightTerminalLeadInquiries?.forEach((obj, index) => {
      setFieldValue(
        `freightTerminalLeadInquiries[${index}].terminal`,
        obj.terminal,
      );
      setFieldValue(
        `freightTerminalLeadInquiries[${index}].talkedTo`,
        obj.talkedTo,
      );
      setFieldValue(
        `freightTerminalLeadInquiries[${index}].updates`,
        obj.updates,
      );
    });
  };

  const updateCallSameDaysDataset = (details: IDSInvestigation) => {
    details.callSameDaysDataset?.forEach((obj, index) => {
      setFieldValue(
        `callSameDaysDataset[${index}].sameDayPro`,
        obj?.sameDayPro,
      );
      setFieldValue(
        `callSameDaysDataset[${index}].deliveryInfo`,
        obj?.deliveryInfo,
      );
    });
  };

  useEffect(() => {
    if (!investigationDetails) return;

    const { investigationFreightLocated, investigationDoneTillTheDay } =
      investigationDetails;
    setIsFreightLocated(investigationFreightLocated);
    setLastStatusNumber(investigationDoneTillTheDay ?? 1);

    ensureDatasetsExist(investigationDetails);
    allFields.forEach((key) => {
      const value =
        key === "pumManifest" && investigationDetails[key] === 0
          ? undefined
          : investigationDetails[key];

      setFieldValue(key, value);
      updateInitValue(key, value);
    });

    investigationDetails?.freightTerminalLeadInquiries?.forEach(
      (obj: IDSFreightTerminalLeadInquiries, index: number) => {
        setFieldValue(
          `freightTerminalLeadInquiries[${index}].terminal`,
          obj.terminal,
        );
        setFieldValue(
          `freightTerminalLeadInquiries[${index}].talkedTo`,
          obj.talkedTo,
        );
        setFieldValue(
          `freightTerminalLeadInquiries[${index}].updates`,
          obj.updates,
        );
      },
    );

    updateFreightTerminalLeadInquiries(investigationDetails);
    updateCallSameDaysDataset(investigationDetails);

    if (investigationDetails.investigationStatus) {
      const stepMap =
        isFreightLocated || investigationDetails?.investigationFreightLocated
          ? FREIGHT_LOCATED_STEP_MAP
          : STEP_STATUS_MAP;

      const stepKey =
        isFreightLocated || investigationDetails?.investigationFreightLocated
          ? investigationDetails?.investigationDoneTillTheDay
          : investigationDetails.investigationStatus;

      setActiveStep(stepMap[stepKey as keyof typeof stepMap]);
      setStatus(investigationDetails.investigationStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationDetails]);

  useEffect(() => {
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);

  // useEffect for epImagingResponse validation
  useEffect(() => {
    // Check if the epImaging value is 'Y' or 'N' and perform the validation
    if (values.epImaging === "Y" || values.epImaging === "N") {
      isValueExist("epImagingResponse"); // Validate epImagingResponse based on epImaging value
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.epImaging]);

  useEffect(() => {
    const adjustedStatus = isFreightLocated
      ? FREIGHT_LOCATED_STEP_MAP[
          lastStatusNumber as keyof typeof FREIGHT_LOCATED_STEP_MAP
        ]
      : STEP_STATUS_MAP[status as keyof typeof STEP_STATUS_MAP];
    // Determine the condition to display the complete button
    const shouldDisplayCompleteBtn = !(
      isFreightLocated ||
      adjustedStatus > activeStep ||
      status === EShortageInvestigationStatus.pendingSrApproval ||
      (!isFreightLocated && status === EShortageInvestigationStatus.complete)
    );

    setShouldShowDayCompleteButton(shouldDisplayCompleteBtn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreightLocated, lastStatusNumber, status, activeStep, STEP_STATUS_MAP]);

  return (
    <div>
      <AccordionDetails className="accordion-container">
        <InvestigationStepper
          activeStep={activeStep}
          steps={AS_INVESTIGATION_WIZARD_STEPS}
          isFreightLocated={isFreightLocated}
          lastStatusNumber={lastStatusNumber}
          status={status}
          setIsCompleting={setIsCompleting}
          setFormFieldsDisabled={setFormFieldsDisabled}
          setIsEditMode={setIsEditMode}
          reset={reset}
          setActiveStep={setActiveStep}
        />
        <FormikProvider key={resetTimestamps} value={form}>
          <>
            {activeStep === STEP_STATUS_MAP[1] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.statusCorrect === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setFieldValue(
                                  "statusCorrect",
                                  e.target.checked ? "Y" : "N",
                                );
                                setFieldValue(
                                  "statusResponse",
                                  e.target.checked ? values.statusResponse : "",
                                  true,
                                );
                              }}
                              disabled={
                                isControlDisabled("statusCorrect") ??
                                formFieldsDisabled
                              }
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />{" "}
                        Confirm the delivery status is correct <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={12} xs={12} className="all-short-entry">
                          {values.statusCorrect === "Y" && (
                            <TextField
                              autoComplete="off"
                              name="statusResponse"
                              value={values.statusResponse ?? ""}
                              fullWidth
                              placeholder="If incorrect what changes were made"
                              error={hasError("statusResponse")}
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                    {getErrorMessage("statusCorrect")}
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.deliveryCode === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setFieldValue(
                                  "deliveryCode",
                                  e.target.checked ? "Y" : "N",
                                );
                                setFieldValue(
                                  "deliveryCodeResponse",
                                  e.target.checked
                                    ? initValues.deliveryCodeResponse
                                    : "",
                                  true,
                                );
                              }}
                              disabled={
                                isControlDisabled("deliveryCode") ??
                                formFieldsDisabled
                              }
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />{" "}
                        Confirm the delivery code matches the ADE and populated
                        Correctly in the F7 <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={12} xs={12} className="all-short-entry">
                          {values.deliveryCode === "Y" && (
                            <TextField
                              autoComplete="off"
                              name="deliveryCodeResponse"
                              value={values.deliveryCodeResponse ?? ""}
                              fullWidth
                              placeholder="If Incorrect What Changes Were Made"
                              error={hasError("deliveryCodeResponse")}
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />
                          )}
                        </Grid>
                      </Grid>
                      {getErrorMessage("deliveryCode")}
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="destinationTerminalLabel">
                        Are there EP’s in imaging? <sup>*</sup> &nbsp;
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={12} xs={12} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="epImaging"
                            name="epImaging"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="epImaging"
                              onChange={(
                                e: React.SyntheticEvent,
                                checked: boolean,
                              ) => {
                                handleChange(e);
                                isValueExist("epImagingResponse");
                                setFieldValue(
                                  "epImagingResponse",
                                  checked && initValues["epImaging"] === "Y"
                                    ? initValues["epImagingResponse"]
                                    : "",
                                  true,
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.epImaging === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="epImaging"
                              onChange={(
                                e: React.SyntheticEvent,
                                checked: boolean,
                              ) => {
                                handleChange(e);
                                isValueExist("epImagingResponse");
                                setFieldValue(
                                  "epImagingResponse",
                                  checked && initValues["epImaging"] === "N"
                                    ? initValues["epImagingResponse"]
                                    : "",
                                  true,
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.epImaging === "N"}
                            />
                          </RadioGroup>
                          {["Y", "N"].includes(values.epImaging ?? "") && (
                            <>
                              <FormControl fullWidth size="small">
                                <FormLabel
                                  id={
                                    values.epImaging === "Y"
                                      ? "epValid"
                                      : "bpFreight"
                                  }
                                  className="mb-10"
                                >
                                  {values.epImaging === "Y"
                                    ? "Confirm the EP’s are valid"
                                    : "Are there BP’s that show the freight"}
                                  <sup> *</sup>
                                </FormLabel>
                                <TextField
                                  autoComplete="off"
                                  value={values.epImagingResponse ?? ""}
                                  key={values.epImaging}
                                  name="epImagingResponse"
                                  placeholder="Describe Steps Taken"
                                  variant="outlined"
                                  error={hasError("epImagingResponse")}
                                  inputProps={{
                                    maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                  }}
                                  onChange={handleChange}
                                  onBlur={(e) => {
                                    handleBlur(e);
                                    isValueExist("epImagingResponse");
                                  }}
                                  disabled={formFieldsDisabled}
                                />
                              </FormControl>
                              {getErrorMessage("epImagingResponse")}
                            </>
                          )}
                          {getErrorMessage("epImaging")}
                          {renderCompletedError("epImagingResponse", "day1")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  {renderF18Note("day1F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[3] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.noMovement === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setFieldValue(
                                  "noMovement",
                                  e.target.checked ? "Y" : "N",
                                );
                                setFieldValue(
                                  "dateLastMovement",
                                  e.target.checked
                                    ? initValues.dateLastMovement
                                    : "",
                                  true,
                                );
                                setFieldValue(
                                  "noMovementSteps",
                                  e.target.checked
                                    ? initValues.noMovementSteps
                                    : "",
                                  true,
                                );
                              }}
                              disabled={
                                isControlDisabled("noMovement") ??
                                formFieldsDisabled
                              }
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Confirm shortage is still open? <sup> *</sup>
                      </FormLabel>
                      {getErrorMessage("noMovement")}
                      {values.noMovement === "Y" && (
                        <Grid container spacing={2}>
                          <Grid item md={4} xs={12} className="all-short-entry">
                            <FormLabel
                              id="destinationTerminal"
                              className="date-label-margin"
                            >
                              Select date of last movement <sup>*</sup>
                            </FormLabel>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                disableFuture
                                value={dayjs(values.dateLastMovement ?? null)}
                                name="dateLastMovement"
                                key={dateKey}
                                open={open}
                                onOpen={() => setOpen(true)}
                                onClose={() => setOpen(false)}
                                onChange={(value) => {
                                  if (
                                    value?.format("MM/DD/YY") === "Invalid Date"
                                  ) {
                                    setDateKey(Date.now().toString());
                                  } else {
                                    setFieldValue(
                                      "dateLastMovement",
                                      value?.format("MM/DD/YY"),
                                      true,
                                    );
                                    setOpen(false);
                                  }
                                }}
                                format="MM/DD/YYYY"
                                slotProps={{
                                  textField: {
                                    error: hasError("dateLastMovement"),
                                    onBlur: handleBlur,
                                    onClick: () => {
                                      setOpen(true);
                                    },
                                    inputProps: {
                                      readOnly: true,
                                    },
                                  },
                                }}
                                disabled={formFieldsDisabled}
                                className="col-md-12"
                              />
                            </LocalizationProvider>

                            {getErrorMessage("dateLastMovement")}
                          </Grid>
                          <Grid item md={8} xs={12} className="all-short-entry">
                            <FormLabel
                              id="noMovementSteps"
                              className="date-label-margin mb-12px"
                            >
                              &nbsp;
                            </FormLabel>

                            <TextField
                              autoComplete="off"
                              name="noMovementSteps"
                              value={values.noMovementSteps ?? ""}
                              fullWidth
                              placeholder="Describe Steps Taken"
                              error={hasError("noMovementSteps")}
                              inputProps={{
                                maxLength: MAX_LENGTH_SUMMARY_TEXT,
                              }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              variant="outlined"
                            />

                            {getErrorMessage("noMovementSteps")}
                            {renderCompletedError("noMovementSteps", "day2")}
                          </Grid>
                        </Grid>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    {renderCheckboxQuestion(
                      "suffixBill",
                      "suffixBillSteps",
                      "Check if a suffix bill or OD400 was cut to move some or all of the freight",
                      {
                        text: "If yes, view the AS exception & confirm it was cleared properly. If no, move to next step.",
                        key: "tooltip2",
                      },
                    )}
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="destinationTerminal">
                        Did the driver spot the trailer somewhere? <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={12} xs={12} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="spotTrailer"
                            name="spotTrailer"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="spotTrailer"
                              onChange={(
                                e: React.SyntheticEvent,
                                checked: boolean,
                              ) => {
                                handleChange(e);
                                setFieldValue(
                                  "freightLeft",
                                  initValues["freightLeft"],
                                  true,
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.spotTrailer === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="spotTrailer"
                              onChange={(e: React.SyntheticEvent) => {
                                handleChange(e);
                                setFieldValue("freightLeft", "", true);
                                setInvalidFields(
                                  invalidFields.filter(
                                    (field) => field !== "freightLeft",
                                  ),
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.spotTrailer === "N"}
                            />
                          </RadioGroup>

                          {values.spotTrailer === "Y" && (
                            <>
                              <FormLabel id="destinationTerminal">
                                Was freight left on board? <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                id="freightLeft"
                                name="freightLeft"
                                fullWidth
                                aria-placeholder="Describe Steps Taken"
                                value={values.freightLeft ?? ""}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    "freightLeft",
                                    "spotTrailer",
                                    STEP_STATUS_MAP[3],
                                  );
                                }}
                                disabled={formFieldsDisabled}
                                className="mt-1"
                                placeholder="Describe Steps Taken"
                                error={hasError("freightLeft")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("spotTrailer")}
                          {getErrorMessage("freightLeft")}
                          {renderCompletedError("freightLeft", "day2")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  {renderF18Note("day2F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[4] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={
                            isControlDisabled("bolCheck") ?? formFieldsDisabled
                          }
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.bolCheck === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setFieldValue(
                                  "bolCheck",
                                  e.target.checked ? "Y" : "N",
                                );
                                setFieldValue(
                                  "bolSteps",
                                  e.target.checked ? initValues.bolSteps : "",
                                  true,
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Check the BOL to confirm completion and check for
                        discrepancies <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={6} xs={12} className="all-short-entry">
                          {values.bolCheck === "Y" && (
                            <>
                              <FormLabel id="bolSteps" className="mt-0 mb-1">
                                Use info on BOL to look for duplicate
                                pros/billing error <sup>*</sup> &nbsp;
                                <TooltipWithClickAway
                                  tooltipText="Option 5 is a good start to look for duplicate pros, but not the only option."
                                  tooltipKey="tooltip3"
                                />
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="bolSteps"
                                value={values.bolSteps ?? ""}
                                fullWidth
                                placeholder="Describe Steps taken"
                                error={hasError("bolSteps")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValueExist("bolSteps");
                                }}
                                disabled={formFieldsDisabled}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("bolCheck")}
                          {getErrorMessage("bolSteps")}
                          {renderCompletedError("bolSteps", "day3")}
                        </Grid>
                        <Grid item md={6} xs={12} className="all-short-entry">
                          {values.bolCheck === "Y" && (
                            <>
                              <FormLabel
                                id="bolDiscrepancies"
                                className="mt-0 mb-1"
                              >
                                List any and all discrepancies <sup>*</sup>{" "}
                                &nbsp;
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="bolDiscrepancies"
                                value={values.bolDiscrepancies ?? ""}
                                fullWidth
                                placeholder="Describe Steps taken"
                                error={hasError("bolDiscrepancies")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValueExist("bolDiscrepancies");
                                }}
                                disabled={formFieldsDisabled}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("bolDiscrepancies")}
                          {renderCompletedError("bolDiscrepancies", "day3")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          data-testid="pumCheckLabel"
                          disabled={
                            isControlDisabled("pumCheck") ?? formFieldsDisabled
                          }
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.pumCheck === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setFieldValue(
                                  "pumCheck",
                                  e.target.checked ? "Y" : "N",
                                );
                                setFieldValue(
                                  "pumManifest",
                                  e.target.checked
                                    ? initValues.pumManifest
                                    : "",
                                  true,
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Check the PUM (Pickup manifest) for discrepancies,
                        shipper load and count information, and proper reporting
                        process <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={4} xs={12} className="all-short-entry">
                          {values.pumCheck === "Y" && (
                            <>
                              <FormLabel
                                id="destinationTerminal"
                                className="mt-0 mb-1"
                              >
                                Pickup Manifest # <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="pumManifest"
                                value={
                                  values.pumManifest
                                    ? `${values.pumManifest}`
                                    : ""
                                }
                                fullWidth
                                placeholder="Enter Pickup Manifest #"
                                type="number"
                                onPaste={handlePaste}
                                onKeyDown={handleKeyDown}
                                onInput={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  handleNumberMaxLength(
                                    e,
                                    MAX_LENGTH_MANIFEST_NUMBER,
                                  );
                                }}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValueExist("pumManifest");
                                }}
                                error={hasError(
                                  "pumManifest",
                                  values.pumManifest === 0,
                                )}
                                disabled={formFieldsDisabled}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("pumCheck")}
                          {getErrorMessage("pumManifest")}
                          {renderCompletedError("pumManifest", "day3")}

                          {values.pumManifest === 0 &&
                            values.pumCheck === "Y" && (
                              <span className="form-error">
                                Invalid pickup manifest#
                              </span>
                            )}
                        </Grid>
                        <Grid item md={8} xs={12} className="all-short-entry">
                          {values.pumCheck === "Y" && (
                            <>
                              <FormLabel
                                id="destinationTerminal"
                                className="mt-0 mb-1"
                              >
                                List any and all discrepancies <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="pumDiscrepancy"
                                value={values.pumDiscrepancy ?? ""}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                error={hasError("pumDiscrepancy")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValueExist("pumDiscrepancy");
                                }}
                                disabled={formFieldsDisabled}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("pumDiscrepancy")}
                          {renderCompletedError("pumDiscrepancy", "day3")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="shipperLoad">
                        Was this a shipper load and count? <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={12} xs={12} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="shipperLoad"
                            name="shipperLoad"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="shipperLoad"
                              onChange={(e: React.SyntheticEvent) => {
                                handleChange(e);
                                setFieldValue(
                                  "shipperDiscrepancy",
                                  initValues["shipperDiscrepancy"],
                                  true,
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.shipperLoad === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="shipperLoad"
                              onChange={(e: React.SyntheticEvent) => {
                                handleChange(e);
                                setFieldValue("shipperDiscrepancy", "", true);
                                setInvalidFields(
                                  invalidFields.filter(
                                    (field) => field !== "shipperDiscrepancy",
                                  ),
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.shipperLoad === "N"}
                            />
                          </RadioGroup>

                          {values.shipperLoad === "Y" && (
                            <>
                              <FormLabel
                                id="shipperDiscrepancy"
                                className="mb-1"
                              >
                                Were there any discrepancies? If so, were they
                                properly reported to the shipper?<sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="shipperDiscrepancy"
                                value={values.shipperDiscrepancy ?? ""}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                error={hasError("shipperDiscrepancy")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValueExist("shipperDiscrepancy");
                                }}
                                disabled={formFieldsDisabled}
                                variant="outlined"
                              />
                            </>
                          )}
                          {getErrorMessage("shipperLoad")}
                          {getErrorMessage("shipperDiscrepancy")}
                          {renderCompletedError("shipperDiscrepancy", "day3")}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    {renderCheckboxQuestion(
                      "lineHaulManifest",
                      "numbersReview",
                      "Check all linehaul manifests – movement and abnormalities.",
                      {
                        text: "Get manifest numbers from the movement screen (F10)",
                        key: "tooltip4",
                      },
                    )}
                  </Grid>

                  <Grid item md={12} xs={12}>
                    {renderCheckboxQuestion(
                      "interviewWorkers",
                      "workerSteps",
                      "Interview the dock worker(s) about movement abnormalities.",
                    )}
                  </Grid>

                  <Grid item md={12} xs={12} className="pt-0">
                    <h3 className="header-text mb-0">
                      Call consignee to confirm what was ordered and received
                      (if anything)
                    </h3>
                  </Grid>

                  {renderSimpleTextField(
                    "consigneePhone",
                    "Phone Number",
                    "day3",
                    {
                      placeholder: "Enter Phone Number",
                      maxLength: MAX_LENGTH_PHONE_NUMBER,
                      gridSize: { md: 3, xs: 12 },
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.ConsigneePhone),
                    },
                  )}

                  {renderSimpleTextField(
                    "consigneeName",
                    "Consignee Name",
                    "day3",
                    {
                      placeholder: "Enter Consignee Name",
                      maxLength: MAX_LENGTH_MEDIUM_ENTRY,
                      gridSize: { md: 3, xs: 12 },
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.ConsigneeName),
                    },
                  )}

                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel id="consigneeShort">
                        Is the consignee still short? <sup>*</sup>
                      </FormLabel>
                      <Grid container spacing={2}>
                        <Grid item md={12} xs={12} className="all-short-entry">
                          <RadioGroup
                            row
                            aria-labelledby="consigneeShort"
                            name="consigneeShort"
                            className="textarea-radio-group"
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                              name="consigneeShort"
                              // onChange={handleChange}
                              onChange={(e: React.SyntheticEvent) => {
                                handleChange(e);
                                setFieldValue(
                                  "itemNumbers",
                                  initValues["itemNumbers"],
                                  true,
                                );
                                setFieldValue(
                                  "orderReceived",
                                  initValues["orderReceived"],
                                  true,
                                );
                                setFieldValue(
                                  "freightLabeled",
                                  initValues["freightLabeled"],
                                  true,
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.consigneeShort === "Y"}
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                              name="consigneeShort"
                              onChange={(e: React.SyntheticEvent) => {
                                handleChange(e);
                                setFieldValue("itemNumbers", "", true);
                                setFieldValue("orderReceived", "", true);
                                setFieldValue("freightLabeled", "", true);
                                setInvalidFields(
                                  invalidFields.filter(
                                    (field) =>
                                      ![
                                        "itemNumbers",
                                        "orderReceived",
                                        "freightLabeled",
                                      ].includes(field),
                                  ),
                                );
                              }}
                              onBlur={handleBlur}
                              disabled={formFieldsDisabled}
                              checked={values.consigneeShort === "N"}
                            />
                          </RadioGroup>
                          {getErrorMessage("consigneeShort")}
                          {values.consigneeShort === "N" && (
                            <NoteBox message="Note: If the consignee is no longer short, the investigation is considered complete once we obtain written confirmation and/or a signed DR." />
                          )}

                          {values.consigneeShort === "Y" && (
                            <>
                              <FormLabel className="mb-10">
                                What are the item numbers received and still
                                missing? <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="itemNumbers"
                                value={values.itemNumbers ?? ""}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    EDSInvestigationFields.ItemNumbers,
                                    EDSInvestigationFields.ConsigneeShort,
                                    STEP_STATUS_MAP[4],
                                  );
                                  isValueExist(
                                    EDSInvestigationFields.ItemNumbers,
                                  );
                                }}
                                disabled={formFieldsDisabled}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                error={hasError("itemNumbers")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                              {getErrorMessage("consigneeShort")}
                              {getErrorMessage("itemNumbers")}
                              {renderCompletedError("itemNumbers", "day3")}

                              <FormLabel className="mb-10">
                                What was ordered and received for each PO?{" "}
                                <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="orderReceived"
                                value={values.orderReceived ?? ""}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    EDSInvestigationFields.OrderReceived,
                                    EDSInvestigationFields.ConsigneeShort,
                                    STEP_STATUS_MAP[4],
                                  );
                                  isValueExist(
                                    EDSInvestigationFields.OrderReceived,
                                  );
                                }}
                                disabled={formFieldsDisabled}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                error={hasError("orderReceived")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                              {getErrorMessage("orderReceived")}
                              {renderCompletedError("orderReceived", "day3")}

                              <FormLabel className="mb-10">
                                Was the freight received labeled for them?{" "}
                                <sup>*</sup>
                              </FormLabel>
                              <TextField
                                autoComplete="off"
                                name="freightLabeled"
                                value={values.freightLabeled ?? ""}
                                onChange={handleChange}
                                onBlur={(e) => {
                                  handleBlur(e);
                                  isValidDependedField(
                                    EDSInvestigationFields.FreightLabeled,
                                    EDSInvestigationFields.ConsigneeShort,
                                    STEP_STATUS_MAP[4],
                                  );
                                  isValueExist(
                                    EDSInvestigationFields.FreightLabeled,
                                  );
                                }}
                                disabled={formFieldsDisabled}
                                fullWidth
                                placeholder="Describe Steps Taken"
                                error={hasError("freightLabeled")}
                                inputProps={{
                                  maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                }}
                                variant="outlined"
                              />
                              {getErrorMessage("freightLabeled")}
                              {renderCompletedError("freightLabeled", "day3")}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12} className="pt-0">
                    <h3 className="header-text mb-0">
                      Call Shipper to confirm what was shipped, how it was
                      packaged and the value?
                    </h3>
                  </Grid>

                  {renderSimpleTextField(
                    "shipperPhone",
                    "Phone Number",
                    "day3",
                    {
                      placeholder: "Enter Phone Number",
                      maxLength: MAX_LENGTH_PHONE_NUMBER,
                      gridSize: { md: 3, xs: 12 },
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.ShipperPhone),
                    },
                  )}

                  {renderSimpleTextField(
                    "shipperName",
                    "Shipper Name",
                    "day3",
                    {
                      placeholder: "Enter Shipper Name",
                      maxLength: MAX_LENGTH_MEDIUM_ENTRY,
                      gridSize: { md: 3, xs: 12 },
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.ShipperName),
                    },
                  )}

                  {renderSimpleTextField(
                    "shippedDetails",
                    "What are the details of what shipped?",
                    "day3",
                    {
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.ShippedDetails),
                    },
                  )}

                  {renderSimpleTextField(
                    "shortShipped",
                    "Was this short shipped?",
                    "day3",
                    {
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.ShortShipped),
                    },
                  )}

                  {renderSimpleTextField(
                    "productValue",
                    "What is the value of the product?",
                    "day3",
                    {
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.ProductValue),
                    },
                  )}

                  {renderSimpleTextField(
                    "multiShipments",
                    "Were there multiple shipments for the same consignee that day?",
                    "day3",
                    {
                      onBlurCallback: () => isValueExist("multiShipments"),
                    },
                  )}

                  <Grid item md={12} xs={12} className="pt-0">
                    <h3 className="header-text mb-0">
                      Call driver about delivery
                    </h3>
                  </Grid>

                  {renderSimpleTextField(
                    "callDriver",
                    "Who did you talk to?",
                    "day3",
                    {
                      placeholder: "Enter Driver Name",
                      onBlurCallback: () => isValueExist("callDriver"),
                    },
                  )}
                  {renderSimpleTextField(
                    "adeDelivery",
                    "What was delivered? Does it match the ADE?",
                    "day3",
                    {
                      onBlurCallback: () => isValueExist("adeDelivery"),
                    },
                  )}

                  {renderSimpleTextField(
                    "freightNose",
                    "Could the freight be in the nose?",
                    "day3",
                    {
                      onBlurCallback: () =>
                        isValueExist(EDSInvestigationFields.FreightNose),
                    },
                  )}

                  {renderSimpleTextField(
                    "outOfOrdinary",
                    "Was there anything out of the ordinary about this delivery?",
                    "day3",
                    {
                      onBlurCallback: () => isValueExist("outOfOrdinary"),
                    },
                  )}

                  {renderF18Note("day3F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[5] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item md={12} xs={12}>
                    {renderCheckboxQuestion(
                      "check7818",
                      "details7818",
                      "Check 78/18 and/or Power BI for any overages (OV, NB, DO) to apply",
                    )}
                  </Grid>

                  {renderSimpleTextField(
                    "cameraFindings",
                    "Check cameras at DT and report findings",
                    "day4",
                    {
                      onBlurCallback: () => isValueExist("cameraFindings"),
                    },
                  )}

                  {renderF18Note("day4F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[6] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={
                            isControlDisabled("callSameDays") ??
                            formFieldsDisabled
                          }
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.callSameDays === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const checked = e.target.checked;
                                setFieldValue(
                                  "callSameDays",
                                  checked ? "Y" : "N",
                                );
                                setFieldValue(
                                  "callSameDaysDataset",
                                  getCallSameDaysDataset(checked),
                                  true,
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Call same day deliveries and pickups <sup>*</sup>
                        <TooltipWithClickAway
                          tooltipText="Use F17 from FB inquiry screen for pickup info. To look up delivery manifest use option 16 from the operations menu"
                          tooltipKey="tooltip9"
                        />
                      </FormLabel>
                      {getErrorMessage("callSameDays")}
                      {values.callSameDays === "Y" && (
                        <Grid
                          key={formKey}
                          container
                          spacing={2}
                          className="filter-margin"
                        >
                          <FieldArray name="callSameDaysDataset">
                            {({ push, remove }) => (
                              <>
                                {_.get(values, "callSameDaysDataset", []).map(
                                  (_d: ICallSameDaysDataset, index: number) => (
                                    <React.Fragment
                                      key={index + ""} /* NOSONAR */
                                    >
                                      <Grid
                                        item
                                        md={12}
                                        xs={12}
                                        className="pt-0"
                                      >
                                        <h3 className="header-text mb-0">
                                          PRO and Delivery Info: {index + 1}
                                        </h3>
                                      </Grid>

                                      <Grid
                                        item
                                        md={3}
                                        xs={12}
                                        className="pt-0"
                                      >
                                        <FormControl className="radio-group question-group ">
                                          <FormLabel>
                                            All same Day Pro's <sup>*</sup>
                                          </FormLabel>
                                          <TextField
                                            autoComplete="off"
                                            id={`tid-${index}`}
                                            name={`callSameDaysDataset[${index}].sameDayPro`}
                                            value={_.get(
                                              values,
                                              `callSameDaysDataset[${index}].sameDayPro`,
                                              "",
                                            )}
                                            onChange={handleChange}
                                            onBlur={(e) => {
                                              handleBlur(e);
                                              isValueExist(
                                                "sameDayPro",
                                                index,
                                                "callSameDaysDataset",
                                              );
                                            }}
                                            disabled={formFieldsDisabled}
                                            fullWidth
                                            className="mt-1"
                                            placeholder="PRO Number"
                                            variant="outlined"
                                            type="text"
                                            onKeyDown={handleTextFieldKeyDown}
                                            onPaste={handlePaste}
                                            inputProps={{ maxLength: 32 }}
                                          />
                                        </FormControl>
                                        {!invalidFields.includes(
                                          "sameDayPro" + index,
                                        ) &&
                                        errors.callSameDaysDataset &&
                                        errors.callSameDaysDataset.length > 0 &&
                                        _.get(
                                          errors,
                                          `callSameDaysDataset[${index}].sameDayPro`,
                                        ) &&
                                        isCompleting ? (
                                          <span className="form-error">
                                            {_.get(
                                              errors,
                                              `callSameDaysDataset[${index}].sameDayPro`,
                                              "",
                                            ).toString()}
                                          </span>
                                        ) : (
                                          ""
                                        )}

                                        {Number.isNaN(
                                          +(
                                            values?.callSameDaysDataset?.[index]
                                              ?.sameDayPro ?? ""
                                          ),
                                        ) &&
                                          values.callSameDays === "Y" && (
                                            <span className="form-error">
                                              Invalid PRO#
                                            </span>
                                          )}
                                        {renderCompletedError(
                                          "sameDayPro" + index,
                                          "day5",
                                          !values?.callSameDaysDataset?.[index]
                                            ?.sameDayPro,
                                        )}
                                      </Grid>

                                      <Grid
                                        item
                                        md={9}
                                        xs={12}
                                        className="pt-0"
                                      >
                                        <FormControl className="radio-group question-group ">
                                          <FormLabel>
                                            Delivery Info?<sup>*</sup>
                                          </FormLabel>
                                          <TextField
                                            autoComplete="off"
                                            id={`deliveryInfo-${index}`}
                                            name={`callSameDaysDataset[${index}].deliveryInfo`}
                                            value={_.get(
                                              values,
                                              `callSameDaysDataset[${index}].deliveryInfo`,
                                              "",
                                            )}
                                            onChange={handleChange}
                                            onBlur={(e) => {
                                              handleBlur(e);
                                              isValueExist(
                                                "deliveryInfo",
                                                index,
                                                "callSameDaysDataset",
                                              );
                                            }}
                                            disabled={formFieldsDisabled}
                                            fullWidth
                                            className="mt-1"
                                            placeholder="Delivery Info"
                                            inputProps={{
                                              maxLength:
                                                MAX_LENGTH_SUMMARY_TEXT,
                                            }}
                                            variant="outlined"
                                          />
                                        </FormControl>

                                        {!invalidFields.includes(
                                          "deliveryInfo" + index,
                                        ) &&
                                        _.get(
                                          errors,
                                          `callSameDaysDataset[${index}].deliveryInfo`,
                                        ) &&
                                        isCompleting ? (
                                          <span className="form-error">
                                            {_.get(
                                              errors,
                                              `callSameDaysDataset[${index}].deliveryInfo`,
                                              "",
                                            ).toString()}
                                          </span>
                                        ) : (
                                          ""
                                        )}
                                        {renderCompletedError(
                                          "deliveryInfo" + index,
                                          "day5",
                                          !values?.callSameDaysDataset?.[index]
                                            ?.deliveryInfo,
                                        )}
                                      </Grid>
                                    </React.Fragment>
                                  ),
                                )}
                                <Grid
                                  item
                                  md={12}
                                  xs={12}
                                  className="btn-group-submit"
                                >
                                  {isShowAddButton() && (
                                    <>
                                      {values.callSameDaysDataset?.length! <
                                        24 && (
                                        <Button
                                          onClick={
                                            proInfoAddButtonDisabled
                                              ? void 0
                                              : () => {
                                                  push({
                                                    sameDayPro: undefined,
                                                    deliveryInfo: undefined,
                                                  });
                                                  proInfoAddRemove();
                                                }
                                          }
                                          variant="contained"
                                          className={`entry ml ${
                                            proInfoAddButtonDisabled
                                              ? "disable-btn disable"
                                              : ""
                                          }`}
                                        >
                                          Add
                                        </Button>
                                      )}
                                      {(values.callSameDaysDataset?.length ??
                                        0) > 1 && (
                                        <Button
                                          onClick={() => {
                                            remove(
                                              values.callSameDaysDataset
                                                ?.length! - 1,
                                            );
                                            proInfoAddRemove("remove");
                                          }}
                                          variant="contained"
                                          className="clear-btn mr-0 ml"
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </Grid>
                              </>
                            )}
                          </FieldArray>
                        </Grid>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <FormControl className="radio-group question-group ">
                      <FormLabel>
                        <FormControlLabel
                          className="checkbox-shoartage"
                          disabled={
                            isControlDisabled("contactTerminals") ??
                            formFieldsDisabled
                          }
                          control={
                            <Checkbox
                              disableRipple
                              checked={values.contactTerminals === "Y"}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const checked = e.target.checked;
                                setFieldValue(
                                  "contactTerminals",
                                  checked ? "Y" : "N",
                                );
                                setFieldValue(
                                  "freightTerminalLeadInquiries",
                                  getFreightTerminalLeadInquiries(checked),
                                  true,
                                );
                              }}
                              sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                            />
                          }
                          label=""
                        />
                        Contact the terminals that handled the freight to find
                        leads and check cameras if applicable <sup>*</sup>
                        <TooltipWithClickAway
                          tooltipText="Use movement screen (F10) to locate terminals."
                          tooltipKey="tooltip6"
                        />
                      </FormLabel>
                    </FormControl>
                    {getErrorMessage("contactTerminals")}
                  </Grid>

                  {values.contactTerminals === "Y" && (
                    <Grid
                      key={formKey}
                      container
                      spacing={2}
                      className="filter-margin custom-container-pos"
                    >
                      <FieldArray name="freightTerminalLeadInquiries">
                        {({ push, remove }) => (
                          <>
                            {_.get(
                              values,
                              "freightTerminalLeadInquiries",
                              [],
                            ).map(
                              (
                                _ft: IDSFreightTerminalLeadInquiries,
                                index: number,
                              ) => (
                                <React.Fragment key={index + ""} /* NOSONAR */>
                                  <Grid item md={12} xs={12} className="pt-0">
                                    <h3 className="header-text mb-0">
                                      Requests for terminal assistance:{" "}
                                      {index + 1}
                                    </h3>
                                  </Grid>

                                  <Grid item md={3} xs={12} className="pt-0">
                                    <FormControl className="radio-group question-group ">
                                      <FormLabel>
                                        TID <sup>*</sup>
                                      </FormLabel>
                                      <TextField
                                        autoComplete="off"
                                        id={`tid-${index}`}
                                        name={`freightTerminalLeadInquiries[${index}].terminal`}
                                        value={_.get(
                                          values,
                                          `freightTerminalLeadInquiries[${index}].terminal`,
                                          "",
                                        )}
                                        onChange={handleChange}
                                        onBlur={(e) => {
                                          handleBlur(e);
                                          isValueExist("terminal", index);
                                        }}
                                        disabled={formFieldsDisabled}
                                        fullWidth
                                        className="mt-1"
                                        placeholder="Enter TID"
                                        error={hasError(
                                          `freightTerminalLeadInquiries[${index}].terminal`,
                                        )}
                                        inputProps={{ maxLength: 5 }}
                                        variant="outlined"
                                      />
                                    </FormControl>
                                    {getValidationErrorMessage(
                                      "terminal",
                                      index,
                                    )}
                                  </Grid>

                                  <Grid item md={3} xs={12} className="pt-0">
                                    <FormControl className="radio-group question-group ">
                                      <FormLabel>
                                        Who did you talk to?<sup>*</sup>
                                      </FormLabel>
                                      <TextField
                                        autoComplete="off"
                                        id={`talkedTo-${index}`}
                                        name={`freightTerminalLeadInquiries[${index}].talkedTo`}
                                        value={_.get(
                                          values,
                                          `freightTerminalLeadInquiries[${index}].talkedTo`,
                                          "",
                                        )}
                                        error={hasError(
                                          `freightTerminalLeadInquiries[${index}].talkedTo`,
                                        )}
                                        onChange={handleChange}
                                        onBlur={(e) => {
                                          handleBlur(e);
                                          isValueExist("talkedTo", index);
                                        }}
                                        disabled={formFieldsDisabled}
                                        fullWidth
                                        className="mt-1"
                                        placeholder="Enter Who did you talk to"
                                        inputProps={{ maxLength: 32 }}
                                        variant="outlined"
                                      />
                                    </FormControl>
                                    {getValidationErrorMessage(
                                      "talkedTo",
                                      index,
                                    )}
                                  </Grid>

                                  <Grid item md={6} xs={12} className="pt-0">
                                    <FormControl className="radio-group question-group ">
                                      <FormLabel>
                                        Provide updates and/or information from
                                        the terminal <sup>*</sup>
                                      </FormLabel>
                                      <TextField
                                        autoComplete="off"
                                        id={`updates-${index}`}
                                        name={`freightTerminalLeadInquiries[${index}].updates`}
                                        value={_.get(
                                          values,
                                          `freightTerminalLeadInquiries[${index}].updates`,
                                          "",
                                        )}
                                        onChange={handleChange}
                                        onBlur={(e) => {
                                          handleBlur(e);
                                          isValueExist("updates", index);
                                        }}
                                        disabled={formFieldsDisabled}
                                        fullWidth
                                        className="mt-1"
                                        placeholder="Enter Details"
                                        error={hasError(
                                          `freightTerminalLeadInquiries[${index}].updates`,
                                        )}
                                        inputProps={{
                                          maxLength: MAX_LENGTH_SUMMARY_TEXT,
                                        }}
                                        variant="outlined"
                                      />
                                    </FormControl>
                                    {getValidationErrorMessage(
                                      "updates",
                                      index,
                                    )}
                                  </Grid>
                                </React.Fragment>
                              ),
                            )}
                            <Grid item md={12} xs={12} className=" ">
                              <NoteBox message="Note | Review strip manifests in imaging prior to contacting the terminal" />
                            </Grid>
                            <Grid
                              item
                              md={12}
                              xs={12}
                              className="btn-group-submit"
                            >
                              {isShowAddButton() && (
                                <>
                                  {values.freightTerminalLeadInquiries
                                    ?.length! < 6 && (
                                    <Button
                                      onClick={
                                        addButtonDisabled
                                          ? void 0
                                          : () => {
                                              push({
                                                terminal: undefined,
                                                talkedTo: undefined,
                                                updates: undefined,
                                              });
                                              addRemove();
                                            }
                                      }
                                      variant="contained"
                                      className={`entry ml ${
                                        addButtonDisabled
                                          ? "disable-btn disable"
                                          : ""
                                      }`}
                                    >
                                      Add
                                    </Button>
                                  )}
                                  {values.freightTerminalLeadInquiries
                                    ?.length !== 1 && (
                                    <Button
                                      onClick={() => {
                                        remove(
                                          values.freightTerminalLeadInquiries
                                            ?.length! - 1,
                                        );
                                        addRemove("remove");
                                      }}
                                      variant="contained"
                                      className="clear-btn mr-0 ml"
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </>
                              )}
                            </Grid>
                          </>
                        )}
                      </FieldArray>
                    </Grid>
                  )}

                  {renderF18Note("day5F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[7] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item md={12} xs={12}>
                    {renderCheckboxQuestion(
                      "followUp",
                      "followUpDetails",
                      "Follow up on all leads/request from previous days",
                    )}
                  </Grid>

                  {renderF18Note("day10F18Note")}
                </Grid>
              </div>
            )}

            {activeStep === STEP_STATUS_MAP[8] && (
              <Day15Investigation
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                values={values}
                setFieldValue={setFieldValue}
                osdNumber={investigationDetails?.osdNumber}
                isControlDisabled={isControlDisabled}
                formFieldsDisabled={formFieldsDisabled}
                getErrorMessage={getErrorMessage}
                isValueExist={isValueExist}
                handleChange={handleChange}
                handleBlur={handleBlur}
                renderF18Note={renderF18Note}
                renderCompletedError={renderCompletedError}
                hasError={hasError}
                initValues={initValues}
                activeStep={activeStep}
                setIsShowSendAPB={setIsShowSendAPB}
              />
            )}

            {activeStep === STEP_STATUS_MAP[9] && (
              <div>
                <Grid container spacing={2} className="filter-margin">
                  <Grid item md={12} xs={12}>
                    {renderCheckboxQuestion(
                      "finalReCheck",
                      "detailsOfSearch",
                      "Final re-check of all leads/requests/overages to confirm the freight cannot be located at this time",
                      undefined,
                      false,
                    )}
                  </Grid>

                  {renderF18Note("day20F18Note")}
                </Grid>
              </div>
            )}
          </>
        </FormikProvider>
        <Grid item md={12} xs={12} className="btn-group-submit">
          {formFieldsDisabled &&
            status !== EShortageInvestigationStatus.complete && (
              <Button
                variant="contained"
                onClick={enableEditing}
                className="assign ml mr-0"
                data-testid="edit-btn"
              >
                Edit
              </Button>
            )}
          {!formFieldsDisabled && (
            <>
              <Button
                onClick={() => reset()}
                variant="contained"
                className="clear-btn mr-0 ml"
                data-testid="clear-btn"
                type="button"
              >
                {isEditMode ? "Cancel" : "Clear"}
              </Button>
              <Button
                variant="contained"
                data-testid="save-btn"
                onClick={() => {
                  saveOrSubmit(EFormAction.save);
                }}
                className={saveCompleteBtnClassName()}
              >
                Save
              </Button>
              {shouldShowDayCompleteButton && (
                <Button
                  data-testid="complete-btn"
                  variant="contained"
                  onClick={() => {
                    saveOrSubmit(EFormAction.submit);
                  }}
                  className={saveCompleteBtnClassName()}
                >
                  Complete Day {activeDay}
                </Button>
              )}
            </>
          )}
          {shouldShowFreightButton && (
            <Button
              onClick={() => handleDialogOpen()}
              variant="contained"
              className="entry ml"
              data-testid="freight-located-btn"
            >
              Freight Located
            </Button>
          )}

          {shouldShowCompleteButton && (
            <Button
              onClick={() => handleFreightAndCompletion(EFormAction.complete)}
              variant="contained"
              className="entry ml"
            >
              Complete & Image
            </Button>
          )}
        </Grid>
      </AccordionDetails>
      <Loader pageLoader={pageLoading}></Loader>

      <div id="ds-investigation-day5" className="hide-me"></div>
      {AS_INVESTIGATION_WIZARD_STEPS.map((day: string, index: number) => (
        <>
          {day === "Day 5" ? (
            <>
              {(callSameDaysChunks || [[]]).map(
                (chunk: ICallSameDaysDataset[], index: number) => {
                  const chunkId = `investigation-${day}-callSameDays-${index}`;
                  return (
                    <div key={chunkId} className="hide-me" id={chunkId}>
                      <DSDay5Template
                        currentDay={day}
                        osdNumber={investigationDetails?.osdNumber}
                        investigationValues={values}
                        callSameDaysChunk={chunk}
                        pageIndex={getPageIndex(index, callSameDaysChunks)}
                      />
                    </div>
                  );
                },
              )}

              {
                <div
                  key={`investigation-${day}-freightTerminal-1`}
                  className="hide-me"
                  id={`investigation-${day}-freightTerminal-${index}`}
                >
                  <DSDay5Template
                    currentDay={day}
                    osdNumber={investigationDetails?.osdNumber}
                    investigationValues={values}
                    freightTerminalChunk={
                      values?.freightTerminalLeadInquiries ?? []
                    }
                  />
                </div>
              }
            </>
          ) : (
            <div key={day} className="hide-me" id={`investigation-${day}`}>
              <DSInvestigationTemplate
                currentDay={day}
                investigationValues={values}
                osdNumber={investigationDetails?.osdNumber}
              />
            </div>
          )}
        </>
      ))}
      <CustomDialog
        message={"Are you sure freight is located?"}
        open={dialogOpen}
        onClose={handleDialogClose}
      />
    </div>
  );
};

export default React.memo(DsInvestigation); // Prevent unnecessary re-renders
